<template>
  <div class="customer-application-services">
    <div class="container-fluid p-2">
      <div class="row g-2">
        <div class="col-sm-12">
          <div class="card shadow-sm border-2 rounded-3 p-1">
            <div class="card-body">
              <h6 class="text-uppercase text-sm fw-bold border-bottom pb-2 mb-3">
                Application Services
              </h6>
              <div class="row g-0">
                <!-- Left Navigation Panel -->
                <div class="col-md-4 col-lg-3 service-panel-container" :class="{ 'panel-closed': !service_panel_open }"
                  v-show="service_panel_open">
                  <div class="service-nav-panel">
                    <div class="service-search mb-3">
                      <div class="position-relative">
                        <input type="text" class="form-control form-control-sm" placeholder="Search services..."
                          id="service-search-input" v-model="search_query" />
                        <i class="fas fa-search position-absolute search-icon"></i>
                      </div>
                    </div>

                    <div class="service-list">
                      <div v-for="service in filtered_services" :key="service.id || service.service_name"
                        class="service-item" :class="{ active: selected_service?.id === service.id }"
                        @click="select_service(service)">
                        <div class="d-flex align-items-start justify-content-between mb-2">
                          <div class="service-name flex-grow-1">
                            {{ service.service_name }}
                          </div>
                          <div class="service-status ms-2">
                            <i class="fas fa-check-circle text-success" v-if="selected_service?.id === service.id"></i>
                          </div>
                        </div>
                        <div class="service-description">
                          {{ service.service_description }}
                        </div>
                        <div class="service-type-badge">
                          <span class="badge">{{ format_service_type(service.service_type) }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Main Content Area -->
                <div class="col-md-8 col-lg-9 main-content-container" :class="{ 'expanded': !service_panel_open }">
                  <div class="main-service-content">
                    <div v-if="!selected_service" class="no-service-selected">
                      <div class="text-center text-muted py-5">
                        <div v-if="!service_panel_open" class="mb-3">
                          <i class="fas fa-list me-2"></i>
                          Click the arrow button above to show services
                        </div>
                        <div v-else>
                          <i class="fas fa-arrow-left me-2"></i>
                          Select a service from the left panel to view details
                        </div>
                      </div>
                    </div>

                    <div v-if="selected_service" class="service-details">
                      <div class="service-header d-flex align-items-center mb-2">
                        <button class="btn btn-bg-secondary shadow-none btn-sm me-3" @click="toggle_service_panel"
                          :title="service_panel_open ? 'Hide Services' : 'Show Services'">
                          <i class="fas" :class="service_panel_open ? 'fa-chevron-left' : 'fa-chevron-right'"></i>
                        </button>
                        <div class="">
                          <label class="fs-5 text-muted fw-bold mb-0">{{ selected_service.service_name }}</label>
                          <p class="text-xs mb-0">{{ selected_service.service_description }}</p>
                        </div>
                      </div>

                      <div class="tab-content mt-3">
                        <div v-if="service_loading" class="text-muted text-xs py-3">Loading service details...</div>
                        <div v-else-if="service_error" class="alert alert-danger py-2 px-3 text-xs">{{ service_error }}
                        </div>
                        <template v-else>
                          <div class="d-flex justify-content-between align-items-center mb-2">
                            <button v-if="hasRisk" class="btn btn-primary btn-sm rounded-pill"
                              :disabled="!risk_user_id || !selected_service?.id"
                              :title="!risk_user_id ? 'Missing customer id for risk assessment' : 'Show Risk Assessment'"
                              @click="show_risk_matrix = !show_risk_matrix">
                              <i class="fa fa-chart-bar me-1"></i>
                              {{ show_risk_matrix ? 'Hide Risk Assessment' : 'Show Risk Assessment' }}
                            </button>
                          </div>

                          <RiskMatrix v-if="hasRisk && show_risk_matrix && risk_user_id && selected_service?.id"
                            :service_id="selected_service.id" :service_name="selected_service.service_name"
                            :user_id="risk_user_id" :application_id="Number(application.id)"
                            :service_type="get_risk_service_type()"
                            :entity_name="entity_name" />

                          <div v-if="pdf_url" class="mb-3">
                            <div class="ratio ratio-16x9 border rounded">
                              <iframe :src="pdf_url" title="Service PDF" frameborder="0"></iframe>
                            </div>
                          </div>
                          <ServiceData :service="selected_service" :response_doc="service_response?.response_doc" :response="service_response"
                            :customer_image="customer_image_base_64" :customerApplicationId="application?.id" />
                        </template>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ServiceData from "@/layouts/data_hub/components/services/service/ServiceData.vue";
import { API_BASE_URL } from "@/config";
import { parseServiceFetchPayload } from "@/layouts/data_hub/components/services/service/serviceFetch";
import { defineAsyncComponent } from "vue";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner.vue";

export default {
  name: "CustomerApplicationServices",
  components: {
    ServiceData,
    RiskMatrix: defineAsyncComponent({
      loader: () => import("@/components/RiskMatrix/RiskMatrix.vue"),
      loadingComponent: LoadingSpinner,
      delay: 200,
    }),
  },
  props: {
    services: {
      type: Array,
      default: () => [],
    },
    application: { type: Object, required: false, default: null },
    entity: { type: Object, required: false, default: null },
    entityType: { type: String, required: false, default: null },
    financial_services: { type: Object, required: false, default: () => ({}) },
  },
  data() {
    return {
      search_query: "",
      selected_service: null,
      service_panel_open: true,
      service_loading: false,
      service_error: null,
      service_response: null,
      pdf_url: null,
      customer_image_base_64: null,
      show_risk_matrix: false,
      overall_severity: null,
      fetched_financial_services: [],
      credit_bureau_services: [],
    };
  },
  computed: {
    risk_user_id() {
      const cands = [
        this.application?.customer_id,
        this.application?.user_id,
        this.application?.raw?.customer_id,
        this.application?.raw?.user_id,
      ];
      const val = cands.find(v => v !== null && v !== undefined);
      return typeof val === 'string' ? Number(val) : val ?? null;
    },
    filtered_services() {
      if (!this.search_query) {
        return this.services;
      }
      return this.services.filter(
        (service) =>
          service.service_name.toLowerCase().includes(this.search_query.toLowerCase()) ||
          service.service_description.toLowerCase().includes(this.search_query.toLowerCase())
      );
    },
    hasRisk() {
      return !!this.overall_severity && String(this.overall_severity).toLowerCase() !== 'unknown';
    },
    entity_name() {
      const isCompany = this.entityType === 'company' || this.entityType === 'enterprise';
      if (isCompany && this.entity?.name) {
        return this.entity.name;
      }
      const isIndividual = this.entityType === 'individual';
      if (isIndividual && (this.entity?.first_name || this.entity?.last_name)) {
        const firstName = this.entity.first_name || '';
        const lastName = this.entity.last_name || '';
        return `${firstName} ${lastName}`.trim();
      }
      return null;
    },
    isSeritiFinanceApplication() {
      const serviceName = (this.selected_service?.service_name || '').toLowerCase();
      return serviceName.includes('seriti') && serviceName.includes('finance');
    },
    hasSeritiFinanceData() {
      return this.financial_services && 
             this.financial_services.finance_application && 
             this.financial_services.finance_application.length > 0;
    }
  },
  emits: ['seriti-finance-click'],
  methods: {
    select_service(service) {
      this.selected_service = service;
      this.show_risk_matrix = false;
      
      const serviceName = (service?.service_name || '').toLowerCase();
      const isSeritiFinance = serviceName.includes('seriti') && serviceName.includes('finance');
      
      if (isSeritiFinance && this.hasSeritiFinanceData) {
        this.$emit('seriti-finance-click');
        return;
      }
      
      this.fetch_service_details();
      this.fetch_overall_severity();
    },
    toggle_service_panel() {
      this.service_panel_open = !this.service_panel_open;
    },
    format_service_type(type) {
      return type ? type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()) : '';
    },
    get_risk_service_type() {
      const service_type = (this.selected_service?.service_type || '').toLowerCase();
      const is_custom_form = service_type === 'system_generated' || service_type === 'system generated';
      return is_custom_form ? 'custom_form' : 'service';
    },
    async fetch_service_details() {
      // Allow IDs of 0 — check for null/undefined instead of truthiness
      if (!this.application || this.application.id == null || !this.selected_service || this.selected_service.id == null) {
        this.service_response = null;
        this.pdf_url = null;
        return;
      }
      const application_id = this.application.id;
      const service_id = this.selected_service.id;
      this.service_loading = true;
      this.service_error = null;
      this.service_response = null;
      this.pdf_url = null;
      this.customer_image_base_64 = null;
      try {
        const TOKEN = localStorage.getItem('token');
        if (!TOKEN) throw new Error('Missing auth token');
        const url = `${API_BASE_URL}/verify_api/data_hub/applications/${application_id}/services/${service_id}/`;
        const resp = await fetch(url, { headers: { 'Content-Type': 'application/json', Authorization: `Token ${TOKEN}` } });
        if (!resp.ok) throw new Error(`Failed to fetch service (${resp.status})`);
        const data = await resp.json();
        const selName = (this.selected_service.service_name || '').toLowerCase();
        this.fetched_financial_services = data.financial_services || [];
        this.credit_bureau_services = data.credit_bureau || [];
        const { pdf_url, customer_image, response, response_doc, documents } = parseServiceFetchPayload(data, service_id, selName);
        this.pdf_url = pdf_url;
        this.customer_image_base_64 = customer_image;
        // Attach documents to response so they're available in the ServiceData component
        this.service_response = { ...response, response_doc, documents };
      } catch (e) {
        this.service_error = e.message || String(e);
      } finally {
        this.service_loading = false;
      }
    },
    async fetch_overall_severity() {
      this.overall_severity = null;
      const service_id = this.selected_service?.id;
      const customer_id = this.risk_user_id;
      const application_id = this.application?.id;
      // Accept 0 as valid ids
      if (service_id == null || customer_id == null || application_id == null) return;
      try {
        const TOKEN = localStorage.getItem('token');

        // Determine which endpoint to use based on service type
        // Custom forms use get_overall_severity, other services use get_overall_service_severity
        const service_type = (this.selected_service?.service_type || '').toLowerCase();
        const is_custom_form = service_type === 'system_generated' || service_type === 'system generated';

        const endpoint = is_custom_form
          ? `get_overall_severity/${service_id}/${customer_id}/${application_id}/`
          : `get_overall_service_severity/${service_id}/${customer_id}/${application_id}/`;

        const url = `${API_BASE_URL}/verify_api/workflow_management_api/${endpoint}`;
        const resp = await fetch(url, { headers: { 'Content-Type': 'application/json', Authorization: `Token ${TOKEN}` } });
        if (!resp.ok) throw new Error('severity fetch failed');
        const data = await resp.json();
        this.overall_severity = data?.severity ?? 'Unknown';
      } catch (e) {
        this.overall_severity = 'Unknown';
      }
    },
    selectServiceAndShowRisk(riskService) {

      const matchingService = this.services.find(service => 
        service.service_name === riskService.service_name ||
        service.id === riskService.service_id ||
        service.raw?.id === riskService.service_id
      );
      
      if (matchingService) {
        this.selected_service = matchingService;
        
        this.service_panel_open = true;

        this.fetch_service_details();
        this.fetch_overall_severity();
        

        setTimeout(() => {
          this.show_risk_matrix = true;
        }, 1000); 
      }
    }
  },
  watch: {
    // Reset state when services prop changes (new entity/application selected)
    services: {
      handler(newServices, oldServices) {
        // Only reset if it's actually a different set of services
        const newIds = (newServices || []).map(s => s.id).join(',');
        const oldIds = (oldServices || []).map(s => s.id).join(',');
        if (newIds !== oldIds) {
          this.selected_service = null;
          this.service_response = null;
          this.pdf_url = null;
          this.customer_image_base_64 = null;
          this.show_risk_matrix = false;
          this.overall_severity = null;
          this.service_error = null;
          this.search_query = "";
        }
      },
      immediate: false,
    },
    // Reset when application changes
    'application.id': {
      handler(newId, oldId) {
        if (newId !== oldId) {
          this.selected_service = null;
          this.service_response = null;
          this.pdf_url = null;
          this.customer_image_base_64 = null;
          this.show_risk_matrix = false;
          this.overall_severity = null;
          this.service_error = null;
          this.search_query = "";
        }
      },
      immediate: false,
    },
  },
};
</script>

<style scoped>
.service-panel-container {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.panel-closed {
  width: 0 !important;
  min-width: 0 !important;
  opacity: 0;
}

.main-content-container {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.main-content-container.expanded {
  flex: 0 0 100%;
  max-width: 100%;
}

.service-nav-panel {
  border-right: 1px solid #e9ecef;
  height: 600px;
  overflow-y: auto;
  padding-right: 20px;
  padding-left: 4px;
}

.service-list {
  max-height: 520px;
  overflow-y: auto;
  padding-right: 4px;
}

.service-item {
  padding: 16px;
  border: 2px solid transparent;
  border-radius: 12px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;
}

.service-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.service-item:hover {
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  border-color: var(--primary-color);
}

.service-item:hover::before {
  opacity: 1;
}

.service-item.active {
  border-color: var(--primary-color);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.service-item.active::before {
  opacity: 1;
}

.service-name {
  font-weight: 600;
  font-size: 15px;
  margin-bottom: 6px;
  color: var(--bs-dark, #212529);
  line-height: 1.4;
}

.service-item.active .service-name {
  color: var(--primary-color);
}

.service-description {
  font-size: 13px;
  color: var(--bs-secondary, #6c757d);
  margin-bottom: 10px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-clamp: 2;
  overflow: hidden;
}

.service-type-badge {
  margin-top: 6px;
  display: flex;
  justify-content: flex-start;
}

.service-type-badge .badge {
  font-size: 11px;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 16px;
  text-transform: capitalize;
  letter-spacing: 0.025em;
  border: 1px solid transparent;
  background: var(--bs-light, #f8f9fa);
  color: var(--bs-dark, #212529);
  transition: all 0.3s ease;
}

.service-item:hover .service-type-badge .badge {
  background: var(--primary-color);
  color: white;
  border-color: var(--primary-color);
}

.service-item.active .service-type-badge .badge {
  background: var(--primary-color);
  color: white;
  border-color: var(--primary-color);
}

.main-service-content {
  padding-left: 20px;
  min-height: 600px;
}

.no-service-selected {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.service-header {
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 5px;
}

.tab-content {
  min-height: 400px;
}

.service-search input {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 10px 12px 10px 40px;
  font-size: 14px;
  transition: all 0.3s ease;
}

.service-search .search-icon {
  top: 50%;
  left: 12px;
  transform: translateY(-50%);
  color: #a0a0a0;
  font-size: 14px;
  pointer-events: none;
}

#service-search-input {
  padding-left: 30px !important;
}

.service-search input:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(var(--primary-color-rgb, 0, 123, 255), 0.1);
  outline: none;
}

.service-search input:focus+.search-icon {
  color: var(--primary-color);
}

.service-search input::placeholder {
  color: #a0a0a0;
  font-style: italic;
}

@media (max-width: 768px) {
  .service-panel-container {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    z-index: 1050;
    background: white;
    box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
    transform: translateX(-100%);
  }

  .service-panel-container:not(.panel-closed) {
    transform: translateX(0);
  }

  .main-content-container {
    flex: 0 0 100% !important;
    max-width: 100% !important;
  }

  .service-nav-panel {
    border-right: none;
    border-bottom: 1px solid #e9ecef;
    height: 100vh;
    max-height: none;
    padding-right: 15px;
    padding-left: 15px;
    margin-bottom: 0;
  }

  .service-list {
    max-height: calc(100vh - 120px);
  }

  .main-service-content {
    padding-left: 0;
  }

  .service-item {
    margin-bottom: 8px;
    padding: 12px;
  }
}
</style>
